import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
  TagsOutlined,
} from '@ant-design/icons';
import { AInput, AModal } from '..';
import api from '../../services/api';
import { formatToBRL, parseCurrencyInput } from '../../shared/utils/functions';
import { useI18n } from '../../shared/i18n';

const { Text } = Typography;

interface IProduct {
  id: string;
  name: string;
  code: number;
  unity: string;
  product_type: number;
  sale_price: number;
}

export interface IRetailSaleProduct {
  id: string;
  product_id: string;
  product_name?: string;
  product_code?: number;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface ITreatmentRetailProductsModalProps {
  open: boolean;
  onClose: () => void;
  items: IRetailSaleProduct[];
  setItems: React.Dispatch<React.SetStateAction<IRetailSaleProduct[]>>;
}

export const TreatmentRetailProductsModal: React.FC<
  ITreatmentRetailProductsModalProps
> = ({ open, onClose, items, setItems }) => {
  const { t } = useI18n();
  const [products, setProducts] = useState<IProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const [quantity, setQuantity] = useState<number>(1);
  const [unitPrice, setUnitPrice] = useState<number>(0);
  const selectedProduct = useMemo(
    () => products.find((product) => product.id === selectedProductId) ?? null,
    [products, selectedProductId],
  );

  const loadProducts = useCallback(async () => {
    try {
      setLoadingProducts(true);
      const response = await api.get('/products/select');
      setProducts(response.data ?? []);
    } catch {
      message.error(t('treatmentProductsModal.retail.loadProductsError'));
    } finally {
      setLoadingProducts(false);
    }
  }, [t]);

  useEffect(() => {
    if (open) {
      loadProducts();
    }
  }, [open, loadProducts]);

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    setUnitPrice(Number(selectedProduct.sale_price || 0));
  }, [selectedProduct]);

  const availableProducts = useMemo(
    () =>
      products.filter(
        (product) =>
          product.product_type === 1 &&
          !items.some((item) => item.product_id === product.id),
      ),
    [products, items],
  );

  const handleAddProduct = useCallback(() => {
    if (!selectedProductId) {
      message.warning(t('treatmentProductsModal.retail.selectProductWarning'));
      return;
    }

    if (quantity <= 0) {
      message.warning(t('treatmentProductsModal.retail.quantityWarning'));
      return;
    }

    if (unitPrice < 0) {
      message.warning(t('treatmentProductsModal.retail.unitPriceWarning'));
      return;
    }

    setItems((current) => [
      ...current,
      {
        id: `${selectedProductId}-${Date.now()}`,
        product_id: selectedProductId,
        product_name: products.find((item) => item.id === selectedProductId)
          ?.name,
        product_code: products.find((item) => item.id === selectedProductId)
          ?.code,
        quantity,
        unit_price: unitPrice,
        total_price: quantity * unitPrice,
      },
    ]);

    setSelectedProductId(null);
    setQuantity(1);
    setUnitPrice(0);
  }, [products, quantity, selectedProductId, setItems, unitPrice, t]);

  const handleSelectProduct = useCallback(
    (value: string) => {
      setSelectedProductId(value);

      const product = products.find((item) => item.id === value);
      setUnitPrice(Number(product?.sale_price || 0));
    },
    [products],
  );

  const handleUnitPriceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setUnitPrice(parseCurrencyInput(event.target.value));
    },
    [],
  );

  const handleUpdateItem = useCallback(
    (id: string, field: 'quantity' | 'unit_price', value: number) => {
      setItems((current) =>
        current.map((item) => {
          if (item.id !== id) {
            return item;
          }

          const nextItem = {
            ...item,
            [field]: value,
          };

          return {
            ...nextItem,
            total_price: nextItem.quantity * nextItem.unit_price,
          };
        }),
      );
    },
    [setItems],
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      setItems((current) => current.filter((item) => item.id !== id));
    },
    [setItems],
  );

  const totalProducts = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalValue = items.reduce((sum, item) => sum + item.total_price, 0);

  return (
    <AModal
      open={open}
      close={onClose}
      title={t('treatmentProductsModal.retail.modalTitle')}
      okText={t('treatmentProductsModal.retail.modalOk')}
      onOk={onClose}
      width={920}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={20}>
        <Card
          type="inner"
          title={
            <Space>
              <ShoppingCartOutlined style={{ color: '#1677ff' }} />
              <span>{t('treatmentProductsModal.retail.buildSaleTitle')}</span>
            </Space>
          }
        >
          <Row gutter={12} align="bottom">
            <Col xs={24} md={12}>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 8 }}
              >
                {t('treatmentProductsModal.retail.product')}
              </Text>
              <Select
                showSearch
                placeholder={t(
                  'treatmentProductsModal.retail.productPlaceholder',
                )}
                style={{ width: '100%' }}
                value={selectedProductId}
                onChange={handleSelectProduct}
                loading={loadingProducts}
                optionFilterProp="children"
                filterOption={(input, option: any) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={availableProducts.map((product) => ({
                  label: `${product.code} - ${product.name}`,
                  value: product.id,
                }))}
              />
            </Col>
            <Col xs={12} md={4}>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 8 }}
              >
                {t('treatmentProductsModal.retail.quantity')}
              </Text>
              <InputNumber
                min={1}
                value={quantity}
                onChange={(value) => setQuantity(Number(value) || 1)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={12} md={4}>
              <Text
                type="secondary"
                style={{ display: 'block', marginBottom: 8 }}
              >
                {t('treatmentProductsModal.retail.unitPrice')}
              </Text>
              <AInput
                value={formatToBRL(unitPrice)}
                onChange={handleUnitPriceChange}
              />
            </Col>
            <Col xs={24} md={4}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddProduct}
                style={{ width: '100%' }}
              >
                {t('treatmentProductsModal.retail.addButton')}
              </Button>
            </Col>
          </Row>

          <div
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 8,
              background: '#f6ffed',
              border: '1px solid #b7eb8f',
            }}
          >
            <Text type="secondary">
              {t('treatmentProductsModal.retail.helper')}
            </Text>
          </div>
        </Card>

        <Card
          type="inner"
          title={
            <Space>
              <TagsOutlined style={{ color: '#52c41a' }} />
              <span>{t('treatmentProductsModal.retail.itemsTitle')}</span>
            </Space>
          }
        >
          {items.length === 0 ? (
            <Empty description={t('treatmentProductsModal.retail.empty')} />
          ) : (
            <Table
              rowKey="id"
              pagination={false}
              scroll={{ x: true }}
              dataSource={items}
              columns={[
                {
                  title: t('treatmentProductsModal.retail.product'),
                  dataIndex: 'product_id',
                  render: (productId: string) => {
                    const product = products.find(
                      (item) => item.id === productId,
                    );
                    return product
                      ? `${product.code} - ${product.name}`
                      : t('treatmentProductsModal.retail.productNotFound');
                  },
                },
                {
                  title: t('treatmentProductsModal.retail.quantity'),
                  dataIndex: 'quantity',
                  width: 120,
                  render: (value: number, record: IRetailSaleProduct) => (
                    <InputNumber
                      min={1}
                      value={value}
                      onChange={(nextValue) =>
                        handleUpdateItem(
                          record.id,
                          'quantity',
                          Number(nextValue) || 1,
                        )
                      }
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: t('treatmentProductsModal.retail.unitPrice'),
                  dataIndex: 'unit_price',
                  width: 160,
                  render: (value: number, record: IRetailSaleProduct) => (
                    <AInput
                      value={formatToBRL(value)}
                      onChange={(event) =>
                        handleUpdateItem(
                          record.id,
                          'unit_price',
                          parseCurrencyInput(event.target.value),
                        )
                      }
                    />
                  ),
                },
                {
                  title: 'Total',
                  dataIndex: 'total_price',
                  width: 140,
                  render: (value: number) => formatToBRL(value),
                },
                {
                  title: t('treatmentProductsModal.retail.actions'),
                  width: 90,
                  render: (_: unknown, record: IRetailSaleProduct) => (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveItem(record.id)}
                    />
                  ),
                },
              ]}
            />
          )}
        </Card>

        <Card bodyStyle={{ padding: '12px 16px' }}>
          <Row justify="space-between">
            <Text strong>
              {t('treatmentProductsModal.retail.detachedItems')}
            </Text>
            <Text strong>{totalProducts}</Text>
          </Row>
          <Row justify="space-between" style={{ marginTop: 8 }}>
            <Text strong>
              {t('treatmentProductsModal.retail.projectedTotal')}
            </Text>
            <Text strong style={{ color: '#1677ff', fontSize: 16 }}>
              {formatToBRL(totalValue)}
            </Text>
          </Row>
        </Card>
      </Space>
    </AModal>
  );
};
